// Slide basis classes using CSS variables for peek effect support
// Mobile and Tablet are fixed, Desktop varies based on slidesPerView from backend

const SLIDE_BASIS_MOBILE_TABLET = 'basis-(--carousel-slide-basis) md:basis-(--carousel-slide-basis-md)';
const SLIDE_BASIS_MOBILE_ALL = 'basis-(--carousel-slide-basis) md:basis-(--carousel-slide-basis-md-1) lg:basis-(--carousel-slide-basis-lg-1)';

// Portrait images are narrower: (3/4)/(4/3) = 9/16 ≈ 0.5625 of landscape width
// Formula accounts for gap: basis = (landscape_basis - gap) * 0.5625 + gap
// Uses --card-layout-gap-desktop directly since lg: breakpoint classes only apply at desktop

// Landscape desktop classes per slidesPerView (fixed strings for Tailwind compilation)
export const SLIDE_BASIS_LANDSCAPE = {
    1: SLIDE_BASIS_MOBILE_ALL,
    2: `${SLIDE_BASIS_MOBILE_TABLET} lg:basis-(--carousel-slide-basis-lg-2)`,
    3: `${SLIDE_BASIS_MOBILE_TABLET} lg:basis-(--carousel-slide-basis-lg-3)`,
    4: `${SLIDE_BASIS_MOBILE_TABLET} lg:basis-(--carousel-slide-basis-lg-4)`,
} as const;

// Portrait desktop classes per slidesPerView (fixed strings for Tailwind compilation)
// Fallback 2.5rem = original --card-layout-gap-desktop value
export const SLIDE_BASIS_PORTRAIT = {
    1: 'basis-(--carousel-slide-basis) md:basis-(--carousel-slide-basis-md-1) lg:basis-[calc((var(--carousel-slide-basis-lg-1)-var(--card-layout-gap-desktop,2.5rem))*0.5625+var(--card-layout-gap-desktop,2.5rem))]',
    2: `${SLIDE_BASIS_MOBILE_TABLET} lg:basis-[calc((var(--carousel-slide-basis-lg-2)-var(--card-layout-gap-desktop,2.5rem))*0.5625+var(--card-layout-gap-desktop,2.5rem))]`,
    3: `${SLIDE_BASIS_MOBILE_TABLET} lg:basis-[calc((var(--carousel-slide-basis-lg-3)-var(--card-layout-gap-desktop,2.5rem))*0.5625+var(--card-layout-gap-desktop,2.5rem))]`,
    4: `${SLIDE_BASIS_MOBILE_TABLET} lg:basis-[calc((var(--carousel-slide-basis-lg-4)-var(--card-layout-gap-desktop,2.5rem))*0.5625+var(--card-layout-gap-desktop,2.5rem))]`,
} as const;

// Square display type classes - all images same width, optimized for full slides + peek
export const SLIDE_BASIS_SQUARE = {
    1: 'basis-(--carousel-slide-basis) md:basis-(--carousel-slide-basis-lg-1-square) lg:basis-(--carousel-slide-basis-lg-1-square)',
    2: 'basis-(--carousel-slide-basis) md:basis-(--carousel-slide-basis-md-square) lg:basis-(--carousel-slide-basis-lg-2-square)',
    3: 'basis-(--carousel-slide-basis) md:basis-(--carousel-slide-basis-md-square) lg:basis-(--carousel-slide-basis-lg-3-square)',
    4: 'basis-(--carousel-slide-basis) md:basis-(--carousel-slide-basis-md-square) lg:basis-(--carousel-slide-basis-lg-4-square)',
} as const;

export type SlidesPerViewKey = 1 | 2 | 3 | 4;

// Responsive sizes attribute for srcset optimization
// Mobile (<640px): 80% basis for all slidesPerView
//   Formula: (viewport - left-padding) * basis - gap * (1 - basis)
// Tablet sm (640-767px): 80% basis for all slidesPerView
// Tablet md (768-1023px): 80% basis for slidesPerView=1, 45% basis for others
export const SIZES_BASE = {
    1: '(min-width: 768px) 584px, (min-width: 640px) 482px, calc((100vw - 16px) * 0.8 - 24px * 0.2)',
    2: '(min-width: 768px) 318px, (min-width: 640px) 482px, calc((100vw - 16px) * 0.8 - 24px * 0.2)',
    3: '(min-width: 768px) 318px, (min-width: 640px) 482px, calc((100vw - 16px) * 0.8 - 24px * 0.2)',
    4: '(min-width: 768px) 318px, (min-width: 640px) 482px, calc((100vw - 16px) * 0.8 - 24px * 0.2)',
} as const;

// Desktop sizes vary by slidesPerView (landscape / portrait)
export const SIZES_DESKTOP = {
    4: {
        landscape: '(min-width: 1920px) 357px, (min-width: 1280px) 291px, (min-width: 1024px) 228px',
        portrait: '(min-width: 1920px) 201px, (min-width: 1280px) 164px, (min-width: 1024px) 128px',
    },
    3: {
        landscape: '(min-width: 1920px) 510px, (min-width: 1280px) 418px, (min-width: 1024px) 332px',
        portrait: '(min-width: 1920px) 287px, (min-width: 1280px) 235px, (min-width: 1024px) 186px',
    },
    2: {
        landscape: '(min-width: 1920px) 800px, (min-width: 1280px) 660px, (min-width: 1024px) 528px',
        portrait: '(min-width: 1920px) 450px, (min-width: 1280px) 371px, (min-width: 1024px) 297px',
    },
    1: {
        landscape: '(min-width: 1920px) 1182px, (min-width: 1280px) 978px, (min-width: 1024px) 786px',
        portrait: '(min-width: 1920px) 665px, (min-width: 1280px) 550px, (min-width: 1024px) 442px',
    },
} as const;

// Square display type sizes - all images same width
// Square uses landscape widths on desktop (lg+), portrait widths below
export const SIZES_DESKTOP_SQUARE = {
    4: '(min-width: 1920px) 357px, (min-width: 1280px) 291px, (min-width: 1024px) 228px',
    3: '(min-width: 1920px) 510px, (min-width: 1280px) 418px, (min-width: 1024px) 332px',
    2: '(min-width: 1920px) 800px, (min-width: 1280px) 660px, (min-width: 1024px) 528px',
    1: '(min-width: 1920px) 1182px, (min-width: 1280px) 978px, (min-width: 1024px) 786px',
} as const;

// Media query for desktop breakpoint (used for art direction)
export const MEDIA_DESKTOP = '(min-width: 1024px)';
